import { ObjectStoreMeta, ObjectStoreSchema } from './ngx-indexed-db.meta';

/**
 * Timeout di sicurezza (ms) per le `open`/upgrade IndexedDB. Se l'operazione
 * resta BLOCCATA da connessioni aperte non chiuse, il browser NON emette
 * `onsuccess`/`onerror` (solo `onblocked`): senza un timeout la Promise
 * resterebbe pendente per sempre → ogni `await` a valle si appende → FREEZE.
 * Il timeout converte l'hang in una rejection gestibile (cache-miss → refetch).
 */
const IDB_OPEN_TIMEOUT_MS = 5000;

export function openDatabase(
  indexedDB: IDBFactory,
  dbName: string,
  version?: number,
  upgradeCallback?: (a: Event, b: IDBDatabase) => void,
): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (!indexedDB) {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const request = indexedDB.open(dbName, version);
    let db: IDBDatabase;

    // ANTI-HANG: settle-guard + timeout (vedi `CreateObjectStore`). Una `open`
    // che richiede un upgrade può restare BLOCCATA da connessioni aperte: in quel
    // caso arriva solo `onblocked` (qui già rigetta), ma il timeout copre anche
    // gli edge-case in cui nessun evento si presenta, evitando hang del tab.
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finishOk = (value: IDBDatabase): void => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      resolve(value);
    };
    const finishErr = (err: Error): void => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      reject(err);
    };
    timer = setTimeout(() => {
      finishErr(new Error(`IndexedDB open timeout (blocked) for '${dbName}'`));
    }, IDB_OPEN_TIMEOUT_MS);

    request.onsuccess = (event: Event) => {
      db = request.result;
      // Ensure this connection won't block future upgrades
      try {
        db.onversionchange = () => {
          try {
            db.close();
          } catch {}
        };
      } catch {}
      finishOk(db);
    };
    request.onerror = (event: Event) => {
      finishErr(new Error(`IndexedDB error: ${request.error}`));
    };
    request.onblocked = () => {
      // Another open connection (possibly in another tab) is blocking this open/upgrade
      finishErr(new Error('IndexedDB open blocked by existing connections'));
    };
    if (typeof upgradeCallback === 'function') {
      request.onupgradeneeded = (event: Event) => {
        const upgradingDb = (event.target as IDBOpenDBRequest).result as IDBDatabase;
        // Attach onversionchange safeguard during upgrade as well
        try {
          upgradingDb.onversionchange = () => {
            try {
              upgradingDb.close();
            } catch {}
          };
        } catch {}
        upgradeCallback(event, upgradingDb);
      };
    }
  });
}

export function CreateObjectStore(
  indexedDB: IDBFactory,
  dbName: string,
  version: number,
  storeSchemas: ObjectStoreMeta[],
  migrationFactory?: () => {
    [key: number]: (db: IDBDatabase, transaction: IDBTransaction) => void;
  },
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (!indexedDB) {
      resolve();
      return;
    }
    const request: IDBOpenDBRequest = indexedDB.open(dbName, version);

    // ANTI-HANG: la Promise DEVE settlarsi sempre. Senza questa guardia un
    // upgrade BLOCCATO da connessioni aperte non chiuse lasciava la Promise
    // pendente per sempre (`onblocked` non chiamava né resolve né reject e
    // `onsuccess`/`onerror` non arrivano in quel caso) → ogni `await` a valle
    // (recovery schema, e a cascata getById/add/getByKey) si appendeva → FREEZE.
    // `finish` è idempotente (un solo settle) e pulisce il timer.
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (err?: unknown): void => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (err) reject(err instanceof Error ? err : new Error(String(err)));
      else resolve();
    };
    timer = setTimeout(() => {
      finish(new Error(`IndexedDB CreateObjectStore timeout (blocked) for '${dbName}'`));
    }, IDB_OPEN_TIMEOUT_MS);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const database: IDBDatabase = (event.target as any).result;

      storeSchemas.forEach((storeSchema: ObjectStoreMeta) => {
        if (!database.objectStoreNames.contains(storeSchema.store)) {
          const objectStore = database.createObjectStore(storeSchema.store, storeSchema.storeConfig);
          storeSchema.storeSchema.forEach((schema: ObjectStoreSchema) => {
            objectStore.createIndex(schema.name, schema.keypath, schema.options);
          });
        }
      });

      const storeMigrations = migrationFactory && migrationFactory();
      if (storeMigrations) {
        Object.keys(storeMigrations)
          .map((k) => parseInt(k, 10))
          .filter((v) => v > event.oldVersion)
          .sort((a, b) => a - b)
          .forEach((v) => {
            storeMigrations[v](database, request.transaction!);
          });
      }

      // Don't close here - let onsuccess handle it after transaction completes
    };

    request.onsuccess = (e: any) => {
      try {
        e.target.result.close();
      } catch {}
      finish();
    };

    request.onerror = (e: any) => {
      // Surface the error in console to aid diagnostics
      console.error('IndexedDB CreateObjectStore error:', e?.target?.error || e);
      finish(new Error(`IndexedDB CreateObjectStore error: ${e?.target?.error || e}`));
    };

    request.onblocked = () => {
      // Un upgrade bloccato da connessioni aperte non chiuse NON deve lasciare la
      // Promise pendente: SETTLIAMO con reject (fail-fast). Il chiamante (recovery
      // schema) degrada a errore gestito → cache-miss → refetch, invece di
      // appendere il main thread. Le connessioni di lettura ora si chiudono dopo
      // ogni transazione (vedi service), quindi il blocco è transitorio; il
      // reject evita comunque ogni rischio di hang.
      console.warn('IndexedDB CreateObjectStore blocked by existing connections');
      finish(new Error(`IndexedDB CreateObjectStore blocked for '${dbName}'`));
    };
  });
}
